<template>
  <section class="dp-layout mr-page-layout">
    <section class="dp-right mr-module">
      <div class="page-header">
        <div>
          <div class="header-title-bar">
            <span class="header-icon-bar"></span>
            <h3>运营统计</h3>
          </div>
          <p class="page-desc">全校预约统计、会议室利用率与趋势分析</p>
        </div>
        <el-tag v-if="schoolName" type="success" size="medium" effect="plain" class="school-badge">
          <i class="el-icon-school"></i> {{ schoolName }}
        </el-tag>
        <span v-else-if="!schoolId" class="school-hint">请在顶部选择学校</span>
      </div>

      <div class="toolbar-filter-bar">
      <el-form inline class="toolbar-filter-form" @submit.native.prevent="loadAll">
        <div class="toolbar-filter-fields">
        <el-form-item label="开始日期">
          <el-date-picker v-model="startDate" type="date" value-format="yyyy-MM-dd" placeholder="开始"
            class="toolbar-field-w-date" />
        </el-form-item>
        <el-form-item label="结束日期">
          <el-date-picker v-model="endDate" type="date" value-format="yyyy-MM-dd" placeholder="结束"
            class="toolbar-field-w-date" />
        </el-form-item>
        <el-form-item label="趋势粒度">
          <el-select v-model="groupBy" class="toolbar-field-w-md">
            <el-option label="按日" value="day" />
            <el-option label="按周" value="week" />
            <el-option label="按月" value="month" />
          </el-select>
        </el-form-item>
        <el-form-item label="快捷">
          <el-button-group>
            <el-button size="small" :disabled="!schoolId" @click="applyDatePreset('week')">本周</el-button>
            <el-button size="small" :disabled="!schoolId" @click="applyDatePreset('month')">本月</el-button>
            <el-button size="small" :disabled="!schoolId" @click="applyDatePreset('last30')">近30天</el-button>
          </el-button-group>
        </el-form-item>
        <el-form-item>
          <el-button type="primary" size="small" icon="el-icon-search" native-type="submit" :disabled="!schoolId">查询</el-button>
        </el-form-item>
        </div>
        <div class="toolbar-filter-actions">
          <el-button v-if="canExportSignIn" size="small" type="warning" icon="el-icon-download"
            :loading="exportSignInLoading" :disabled="!schoolId" @click="exportSignIn">
            导出签到报表
          </el-button>
        </div>
      </el-form>
      </div>

      <div v-if="!schoolId" class="empty-tip">请在顶部选择学校后查看统计数据</div>

      <div v-else v-loading="loading">
        <div class="mr-stat-grid">
          <div v-for="item in statCards" :key="item.key"
            :class="['mr-stat-card', item.accent ? `mr-stat-card--${item.accent}` : '', item.clickable ? 'mr-stat-card--clickable' : '']"
            @click="onStatCardClick(item)">
            <div class="mr-stat-value">{{ item.value }}</div>
            <div class="mr-stat-label">{{ item.label }}</div>
            <div v-if="item.hint" class="mr-stat-hint">{{ item.hint }}</div>
          </div>
        </div>

        <el-row :gutter="16" class="mr-chart-row">
          <el-col :span="14">
            <el-card shadow="never" class="mr-panel">
              <div slot="header">预约趋势</div>
              <div ref="trendChart" class="mr-chart-box"></div>
            </el-card>
          </el-col>
          <el-col :span="10">
            <el-card shadow="never" class="mr-panel">
              <div slot="header">会议室利用率排行 <span class="mr-panel-sub">点击行查看该室会议</span></div>
              <el-table :data="utilizationList" size="small" max-height="320" border class="mr-table mr-util-table"
                @row-click="onUtilRowClick">
                <el-table-column prop="RoomName" label="会议室" min-width="90" show-overflow-tooltip />
                <el-table-column label="利用率" min-width="130">
                  <template slot-scope="scope">
                    <el-progress :percentage="Math.min(scope.row.UtilizationRate, 100)"
                      :color="progressColor(scope.row.UtilizationRate)" :stroke-width="10" />
                  </template>
                </el-table-column>
                <el-table-column prop="BookingCount" label="次数" width="56" align="center" />
              </el-table>
            </el-card>
          </el-col>
        </el-row>
      </div>
    </section>
  </section>
</template>

<script>
import echarts from 'echarts';
import axios from 'axios';
import { BaseApiUrl } from '../../api/base';
import { resolveActiveSchoolId } from '../../utils/deptCascaderProps';
import { getButtonList } from '../../utils/routeButtonList';

const apiBase = BaseApiUrl;
const CHART_PRIMARY = '#3B82F6';
const CHART_ACCENT = '#F97316';

function fmtDate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export default {
  name: 'MeetingRoomDashboard',
  data() {
    const today = new Date();
    const end = fmtDate(today);
    const startDate = new Date(today);
    startDate.setDate(startDate.getDate() - 30);
    const start = fmtDate(startDate);

    return {
      startDate: start,
      endDate: end,
      groupBy: 'day',
      loading: false,
      stats: {},
      utilizationList: [],
      trendList: [],
      chartInst: null,
      buttonList: [],
      exportSignInLoading: false
    };
  },
  computed: {
    schoolId() {
      return resolveActiveSchoolId(this);
    },
    schoolName() {
      return this.$store.state.activeSchoolName || '';
    },
    canExportSignIn() {
      return (this.buttonList || []).some(b => b.Func === 'exportSignIn');
    },
    statCards() {
      const s = this.stats || {};
      const pending = s.PendingBookings || 0;
      return [
        { key: 'total', label: '总预约', value: s.TotalBookings || 0 },
        { key: 'confirmed', label: '有效预约', value: s.ConfirmedBookings || 0, accent: 'success' },
        {
          key: 'pending',
          label: '待审核',
          value: pending,
          accent: 'warning',
          clickable: pending > 0,
          hint: pending > 0 ? '点击查看' : ''
        },
        { key: 'cancelled', label: '已取消', value: s.CancelledBookings || 0 },
        { key: 'avg', label: '平均时长(分)', value: s.AvgDurationMinutes || 0 },
        { key: 'minutes', label: '总时长(分)', value: s.TotalMeetingMinutes || 0 },
        { key: 'noshow', label: '爽约', value: s.NoShowBookings || 0, accent: 'danger' },
        { key: 'signin', label: '签到率(%)', value: s.SignInRate || 0, accent: 'primary' }
      ];
    },
    queryParams() {
      return {
        schoolId: this.schoolId,
        campusId: 0,
        startDate: this.startDate,
        endDate: this.endDate,
        groupBy: this.groupBy
      };
    }
  },
  watch: {
    schoolId(val, oldVal) {
      if (val === oldVal) return;
      if (val) this.loadAll();
      else {
        this.stats = {};
        this.utilizationList = [];
        this.trendList = [];
      }
    }
  },
  mounted() {
    const routers = window.localStorage.router ? JSON.parse(window.localStorage.router) : [];
    this.buttonList = getButtonList(this.$route.path, routers);
    window.addEventListener('resize', this.handleResize);
    if (this.schoolId) this.loadAll();
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize);
    if (this.chartInst) this.chartInst.dispose();
  },
  methods: {
    applyDatePreset(preset) {
      const today = new Date();
      const end = fmtDate(today);
      let start;
      if (preset === 'week') {
        const d = new Date(today);
        const day = d.getDay() || 7;
        d.setDate(d.getDate() - day + 1);
        start = fmtDate(d);
      } else if (preset === 'month') {
        start = fmtDate(new Date(today.getFullYear(), today.getMonth(), 1));
      } else {
        const d = new Date(today);
        d.setDate(d.getDate() - 30);
        start = fmtDate(d);
      }
      this.startDate = start;
      this.endDate = end;
      this.loadAll();
    },
    onStatCardClick(item) {
      if (!item.clickable) return;
      if (item.key === 'pending') {
        this.$router.push({ path: '/MeetingRoom/BookingList', query: { tab: 'pending' } });
      }
    },
    onUtilRowClick(row) {
      if (!row || !row.RoomId) return;
      this.$router.push({
        path: '/MeetingRoom/BookingList',
        query: { roomId: row.RoomId }
      });
    },
    loadAll() {
      if (!this.schoolId) return;
      this.loading = true;
      Promise.all([
        axios.get(`${apiBase}/api/MeetingRoomDashboard/GetStats`, { params: this.queryParams }),
        axios.get(`${apiBase}/api/MeetingRoomDashboard/GetUtilization`, { params: this.queryParams }),
        axios.get(`${apiBase}/api/MeetingRoomDashboard/GetTrend`, { params: this.queryParams })
      ]).then(([statsRes, utilRes, trendRes]) => {
        this.loading = false;
        if (statsRes.data.success) this.stats = statsRes.data.response || {};
        if (utilRes.data.success) this.utilizationList = utilRes.data.response || [];
        if (trendRes.data.success) {
          this.trendList = trendRes.data.response || [];
          this.$nextTick(() => this.renderChart());
        }
      }).catch(() => { this.loading = false; });
    },
    exportSignIn() {
      if (!this.schoolId || this.exportSignInLoading) return;
      this.exportSignInLoading = true;
      axios.get(`${apiBase}/api/MeetingBooking/ExportSignInReport`, {
        params: this.queryParams,
        responseType: 'blob'
      }).then(res => {
        const blob = new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `会议签到报表_${Date.now()}.xlsx`;
        link.click();
        window.URL.revokeObjectURL(url);
        this.$message.success('导出成功，文件已开始下载');
      }).catch(() => this.$message.error('导出失败'))
        .finally(() => { this.exportSignInLoading = false; });
    },
    renderChart() {
      if (!this.$refs.trendChart) return;
      if (!this.chartInst) this.chartInst = echarts.init(this.$refs.trendChart);
      const dates = this.trendList.map(t => t.Date);
      this.chartInst.setOption({
        tooltip: { trigger: 'axis' },
        legend: { data: ['预约次数', '会议分钟'] },
        grid: { left: 48, right: 48, bottom: 32, top: 36 },
        xAxis: {
          type: 'category',
          data: dates,
          axisLabel: { rotate: dates.length > 10 ? 30 : 0, color: '#64748b' }
        },
        yAxis: [
          { type: 'value', name: '次数', splitLine: { lineStyle: { color: '#e2e8f0' } } },
          { type: 'value', name: '分钟', splitLine: { show: false } }
        ],
        series: [
          {
            name: '预约次数',
            type: 'bar',
            data: this.trendList.map(t => t.BookingCount),
            itemStyle: { color: CHART_PRIMARY, borderRadius: [4, 4, 0, 0] }
          },
          {
            name: '会议分钟',
            type: 'line',
            yAxisIndex: 1,
            smooth: true,
            data: this.trendList.map(t => t.MeetingMinutes),
            itemStyle: { color: CHART_ACCENT },
            lineStyle: { width: 2 }
          }
        ]
      });
    },
    handleResize() {
      if (this.chartInst) this.chartInst.resize();
    },
    progressColor(rate) {
      if (rate >= 80) return '#DC2626';
      if (rate >= 50) return '#F97316';
      return '#3B82F6';
    }
  }
};
</script>

<style scoped>
.mr-stat-card--clickable {
  cursor: pointer;
  transition: box-shadow 0.2s, transform 0.15s;
}
.mr-stat-card--clickable:hover {
  box-shadow: 0 4px 12px rgba(249, 115, 22, 0.2);
  transform: translateY(-1px);
}
.mr-stat-hint {
  margin-top: 4px;
  font-size: 11px;
  color: #f97316;
}
.mr-panel-sub {
  font-size: 12px;
  font-weight: normal;
  color: #94a3b8;
}
.mr-util-table >>> tbody tr {
  cursor: pointer;
}
</style>
